import { particleBatch, setParticle, finishParticles } from "./particles";
import * as THREE from "three";
import type { MatchState } from "../game/match-controller";
import { OPPONENT_Z, PLAYER_Z } from "../game/world";

const tiers = ["S", "M", "L"] as const;
// T1 stub: one ball size now. T2 replaces the tier meshes.
const BALL_VISUAL_TIER = "S" as const;
export class ProjectileVisuals extends THREE.Group {
  readonly balls: Record<"S" | "M" | "L", THREE.InstancedMesh>;
  readonly shadows: THREE.InstancedMesh;
  readonly trails: THREE.InstancedMesh;
  readonly dots = particleBatch(60);
  readonly entities = new Map<string, THREE.Object3D>();
  constructor() {
    super();
    const make = (tier: "S" | "M" | "L") => {
      const geometry = new THREE.SphereGeometry(1, 16, 8);
      const pos = geometry.getAttribute("position");
      const colors = [];
      for (let i = 0; i < pos.count; i++) {
        const band =
          tier === "M"
            ? Math.abs(pos.getY(i)) < 0.18
            : tier === "L" && Math.abs(Math.abs(pos.getY(i)) - 0.38) < 0.16;
        const color = new THREE.Color(band ? 0x8b7cd7 : 0xffffff);
        colors.push(color.r, color.g, color.b);
      }
      geometry.setAttribute(
        "color",
        new THREE.Float32BufferAttribute(colors, 3),
      );
      const mesh = new THREE.InstancedMesh(
        geometry,
        new THREE.MeshToonMaterial({ vertexColors: true }),
        10,
      );
      mesh.count = 0;
      mesh.frustumCulled = false;
      this.add(mesh);
      return mesh;
    };
    this.balls = { S: make("S"), M: make("M"), L: make("L") };
    this.shadows = new THREE.InstancedMesh(
      new THREE.CircleGeometry(1, 16),
      new THREE.MeshBasicMaterial({
        color: 0x7a74b8,
        transparent: true,
        opacity: 0.2,
        depthWrite: false,
      }),
      10,
    );
    this.trails = new THREE.InstancedMesh(
      new THREE.CylinderGeometry(0.07, 0.01, 0.6, 6),
      new THREE.MeshBasicMaterial({
        color: 0xd6e5ff,
        transparent: true,
        opacity: 0.6,
        depthWrite: false,
      }),
      10,
    );
    this.add(this.shadows, this.trails, this.dots);
    this.shadows.frustumCulled = false;
    this.trails.frustumCulled = false;
  }
  update(match: MatchState | null, camera: THREE.Camera): void {
    this.entities.clear();
    this.dots.count = 0;
    for (const tier of tiers) this.balls[tier].count = 0;
    this.shadows.count = 0;
    this.trails.count = 0;
    if (!match) return;
    const dummy = new THREE.Object3D();
    for (const p of match.projectiles.slice(0, 10)) {
      const progress = Math.max(
        0,
        Math.min(1, (match.tick - p.startTick) / (p.endTick - p.startTick)),
      );
      const start = p.thrower === "player" ? PLAYER_Z : OPPONENT_Z,
        end = p.target === "player" ? PLAYER_Z : OPPONENT_Z;
      const z = start + (end - start) * progress,
        y = 2 + progress * (1 - progress) * 4;
      const x = p.lane === "left" ? -0.55 : p.lane === "right" ? 0.55 : 0;
      // Cap at 60% of the 0.64 m head width (opponent is scaled 0.86).
      const radius = Math.min(
        { S: 0.175, M: 0.275, L: 0.4 }[BALL_VISUAL_TIER] * 0.75,
        (0.64 * (p.target === "opponent" ? 0.86 : 1) * 0.6) / 2,
      );
      const mesh = this.balls[BALL_VISUAL_TIER];
      dummy.position.set(x, y, z);
      dummy.rotation.set(progress * 3 * Math.PI, progress * 2 * Math.PI, 0);
      dummy.scale.setScalar(radius);
      dummy.updateMatrix();
      mesh.setMatrixAt(mesh.count++, dummy.matrix);
      const proxy = new THREE.Mesh(mesh.geometry, mesh.material);
      proxy.position.copy(dummy.position);
      proxy.rotation.copy(dummy.rotation);
      proxy.scale.copy(dummy.scale);
      this.entities.set(`ball:${p.id}`, proxy);
      dummy.position.set(x, 0.014, z);
      dummy.rotation.set(-Math.PI / 2, 0, 0);
      dummy.scale.setScalar(radius * (1 - 0.5 * Math.min(1, y / 3)));
      dummy.updateMatrix();
      this.shadows.setMatrixAt(this.shadows.count++, dummy.matrix);
      const shadowProxy = new THREE.Mesh(
        this.shadows.geometry,
        this.shadows.material,
      );
      shadowProxy.matrix.copy(dummy.matrix);
      shadowProxy.matrixAutoUpdate = false;
      this.entities.set(`ball:${p.id}:shadow`, shadowProxy);
      dummy.position.set(x, y, z - Math.sign(end - start) * 0.3);
      dummy.rotation.set(Math.PI / 2, 0, 0);
      dummy.scale.setScalar(1);
      dummy.updateMatrix();
      this.trails.setMatrixAt(this.trails.count++, dummy.matrix);
      const trailProxy = new THREE.Mesh(
        this.trails.geometry,
        this.trails.material,
      );
      trailProxy.matrix.copy(dummy.matrix);
      trailProxy.matrixAutoUpdate = false;
      this.entities.set(`ball:${p.id}:trail`, trailProxy);
    }
    if (match)
      for (const p of match.projectiles.slice(0, 10)) {
        const progress = Math.max(
            0,
            Math.min(1, (match.tick - p.startTick) / (p.endTick - p.startTick)),
          ),
          start = p.thrower === "player" ? PLAYER_Z : OPPONENT_Z,
          end = p.target === "player" ? PLAYER_Z : OPPONENT_Z,
          z = start + (end - start) * progress,
          y = 2 + progress * (1 - progress) * 4;
        for (let j = 0; j < 6; j++) {
          const a = progress * 20 + j * 1.2;
          dummy.position.set(
            Math.cos(a) * 0.09,
            y + Math.sin(a) * 0.09,
            z - Math.sign(end - start) * j * 0.1,
          );
          dummy.quaternion.copy(camera.quaternion);
          dummy.scale.setScalar(0.06 * (1 - j / 8));
          dummy.updateMatrix();
          setParticle(
            this.dots,
            this.dots.count++,
            dummy.matrix,
            0,
            0xb5d5ff,
            1 - j / 8,
          );
        }
      }
    finishParticles(this.dots);
    for (const mesh of [
      ...Object.values(this.balls),
      this.shadows,
      this.trails,
    ])
      mesh.instanceMatrix.needsUpdate = true;
  }
}
